import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeatureModuleAuth } from '@core/types';
import { Store } from '@ngrx/store';
import { filter, map, Observable, shareReplay } from 'rxjs';
import { entitlementsSelectData } from '../entitlements-store/entitlements.selectors';
import { EntitlementLevelEnum, FeatureMappingEnum } from '../types/entitlements.types';

/**
 * Provides information about user entitlements
 * Subscribes to the selectors to get entitlements data
 * Provides a function to validate if the user has the entitlement
 */
@Injectable({ providedIn: 'root' })
export class EntitlementsService {
    private _store = inject(Store);

    // destroy reference
    private _destroy$ = inject(DestroyRef);

    // Entitlements data to validate
    private _entitlements: FeatureModuleAuth[] = [];

    // Emits when entitlements are loaded
    readonly ready$: Observable<boolean>;

    constructor() {
        // Subscribe to get user entitlements
        this._store
            .select(entitlementsSelectData)
            .pipe(takeUntilDestroyed(this._destroy$), filter(Boolean), shareReplay(1))
            .subscribe(data => (this._entitlements = data));

        this.ready$ = this._store.select(entitlementsSelectData).pipe(
            takeUntilDestroyed(this._destroy$),
            map(data => !!data?.length)
        );
    }

    /**
     * Search for the entitlement
     * @param featureId The id of the feature to search (FeatureMapping enum)
     * @param level Level of access in the FeatureModuleAuth object (feature, nation, product, scheduler)
     */
    public hasEntitlement(feature: FeatureMappingEnum, level: EntitlementLevelEnum): boolean {
        const userEntitlements: FeatureModuleAuth | undefined = this._entitlements?.find(f => f.id === feature);

        if (!userEntitlements) return false;

        switch (level) {
            case EntitlementLevelEnum.FEATURE:
                return userEntitlements.nations.some(nation => nation?.products?.length);

            /** Implement Nation and Product access validation when it is needed 
                    case EntitlementLevelEnum.NATION:
                        return userEntitlements.nations?.length > 0;

                    case EntitlementLevelEnum.PRODUCT:
                        return userEntitlements.nations?.some(n => n?.products?.length && n?.products?.length > 0) ?? false; */

            case EntitlementLevelEnum.REPORT:
                return userEntitlements.nations?.some(n => n?.products?.some(p => p?.reports.length));

            case EntitlementLevelEnum.SCHEDULER:
                return userEntitlements.nations?.some(n => n?.products?.some(p => p?.hasSchedulerAccess === true)) ?? false;

            default:
                return false;
        }
    }
}
